import React, { useState, useEffect, useRef } from "react";
import { MessageCircle, X, Maximize, Minimize, Send, Paperclip, FileText, BadgeInfo, ArrowUpRight, ThumbsUp, ThumbsDown } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from 'uuid';
import { isAdmin } from "../services/authService";
import { buildApiUrl } from "../services/apiConfig";

console.log('WealthChatBot module loaded - VERSION 2.0 with table alignment fixes');

const WealthChatBot = ({ cust_id, use_case_id }) => {
    const API_HOST = process.env.REACT_APP_API_URL || "50.17.67.70";
    const useCaseId = use_case_id;
    
    
    const [isOpen, setIsOpen] = useState(false);
    const [isClosing, setIsClosing] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [isBotTyping, setIsBotTyping] = useState(false);
    const [messages, setMessages] = useState([
        {
            text: "Hello! I'm your wealth management assistant. How can I help you today?",
            sender: "bot"
        }
    ]);
    const [input, setInput] = useState("");
    const [files, setFiles] = useState([]);
    const [queryPopupIndex, setQueryPopupIndex] = useState(null);
    const [userIsAdmin, setUserIsAdmin] = useState(false);
    const [ratedMessages, setRatedMessages] = useState({});
    const [userId, setUserId] = useState();
    const [isUploading, setIsUploading] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState(null);
    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const popupRef = useRef(null);
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        setUserIsAdmin(isAdmin());
    }, []);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, isExpanded]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                setQueryPopupIndex(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const styleText = (text) => {
        console.log('styleText called with text length:', text?.length);
        let formattedText = text;

        if (text.includes('|') && text.includes('\n')) {
            console.log('TABLE DETECTED - Processing markdown table');
            const lines = text.split('\n');
            let inTable = false;
            let tableHTML = '<div class="w-full overflow-x-auto my-2.5"><table class="w-full border-collapse text-sm">';
            let processedLines = [];
            let headerCells = [];

            for (let i = 0; i < lines.length; i++) {
                const line = lines[i].trim();

                if (line.includes('|')) {
                    if (!inTable) {
                        inTable = true;
                        tableHTML = '<div class="w-full overflow-x-auto my-2.5"><table class="w-full border-collapse text-sm">';
                    }

                    const cells = line.split('|').map(cell => cell.trim()).filter(cell => cell);

                    if (line.includes('---')) {
                        continue;
                    }

                    // Store header cells to determine alignment
                    if (i === 0 || (i === 1 && lines[0].includes('|'))) {
                        headerCells = cells;
                        console.log('Header cells captured:', headerCells);
                    }

                    tableHTML += '<tr>';
                    cells.forEach((cell, cellIndex) => {
                        // Determine alignment based on column header
                        const header = headerCells[cellIndex] || '';
                        const isNumericColumn = ['Quantity', 'Purchase Price', 'Current Price', 'Value'].includes(header);
                        const alignmentClass = isNumericColumn ? 'text-right' : 'text-left';
                        console.log(`Cell ${cellIndex}: "${cell}", header: "${header}", isNumeric: ${isNumericColumn}, class: ${alignmentClass}`);
                        
                        if (i === 0 || (i === 2 && lines[1].includes('---'))) {
                            tableHTML += `<th class="p-2 ${alignmentClass} border border-gray-200 bg-gray-50 font-semibold">${cell}</th>`;
                        } else {
                            tableHTML += `<td class="p-2 ${alignmentClass} border border-gray-200">${cell}</td>`;
                        }
                    });
                    tableHTML += '</tr>';
                } else {
                    if (inTable) {
                        tableHTML += '</table></div>';
                        processedLines.push(tableHTML);
                        inTable = false;
                    }
                    processedLines.push(line);
                }
            }

            if (inTable) {
                tableHTML += '</table></div>';
                processedLines.push(tableHTML);
            }

            formattedText = processedLines.join('\n');
        }

        return formattedText
            .replace(/#### (.*?)(\n|$)/g, "<h4 class='text-base font-semibold my-2'>$1</h4>")
            .replace(/### (.*?)(\n|$)/g, "<h3 class='text-lg font-semibold my-2'>$1</h3>")
            .replace(/## (.*?)(\n|$)/g, "<h2 class='text-xl font-semibold my-2'>$1</h2>")
            .replace(/# (.*?)(\n|$)/g, "<h1 class='text-2xl font-bold my-2'>$1</h1>")
            .replace(/\*\*(.*?)\*\*/g, "<b>$1</b>")
            .replace(/\*(.*?)\*/g, "<i>$1</i>")
            .replace(/~~(.*?)~~/g, "<s>$1</s>")
            .replace(/\n/g, "<br>");
    };

    const toggleQueryPopup = (index) => {
        setQueryPopupIndex(prevIndex => (prevIndex === index ? null : index));
    };

    const handleClose = () => {
        setIsClosing(true);
        setTimeout(() => {
            setIsOpen(false);
            setIsClosing(false);
            setIsExpanded(false);
        }, 300);
    };

    const handleExpand = () => {
        setIsExpanded(prevState => !prevState);
    };

    useEffect(() => {
        const storedUserId = sessionStorage.getItem('user_id');
        setUserId(storedUserId || 'U1757535255');
    }, []);

    function getOrGenerateThreadID() {
        let threadID = sessionStorage.getItem("wealth_thread_id");
        if (!threadID) {
            threadID = uuidv4();
            sessionStorage.setItem("wealth_thread_id", threadID);
        }
        return threadID;
    }

    const getPageName = () => {
        const path = location.pathname.substring(1);
        if (!path) return 'WealthManagement';
        return path.split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join('')
            || 'WealthManagement';
    };

    const handleSendMessage = async () => {
        if (input.trim() === "" && !fileInputRef.current.files.length) return;

        const userMessage = { text: input, sender: "user" };
        setMessages(prevMessages => [...prevMessages, userMessage]);
        setQueryPopupIndex(null);
        setInput("");
        setIsBotTyping(true);

        // const apiEndpoint = `http://${API_HOST}:5010/api/v1/nova/Nova_chat_stream`;
        const apiEndpoint = buildApiUrl('NOVA', '/Nova_chat_stream');

        try {
            const formData = new FormData();
            formData.append("query", input);
            formData.append("cust_id", cust_id);
            formData.append("advisor_id", userId);
            formData.append("page_name", getPageName());
            formData.append("use_case_id", useCaseId);
            
            console.log("Sending to backend - use_case_id:", useCaseId, "cust_id:", cust_id);
            
            const threadId = getOrGenerateThreadID();
            formData.append("thread_id", threadId);

            files.forEach(file => {
                    formData.append("files", file);
                    console.log("File appended to FormData:", file.name);
            });
            console.log("FormData prepared:", {
                query: input,
                cust_id: cust_id,
                advisor_id: userId,
                page_name: getPageName(),
                use_case: getPageName(),
                thread_id: threadId
            });

            const response = await fetch(apiEndpoint, {
                method: "POST",
                body: formData,
            });
            if (!response.ok) {
                throw new Error(`Server responded with ${response.status}`);
            }

            setMessages(prevMessages => [...prevMessages, {
                text: "",
                sender: "bot",
                isStreaming: true
            }]);

            let accumulatedText = "";
            let sqlQuery = null;
            try {
                const { createParser } = await import('eventsource-parser');

                const parser = createParser({
                    onEvent: (event) => {
                        console.log("🔥 Event received:", event.event, "Data length:", event.data?.length);
                        const type = event.event;
                        if (type === "filler" || type === "interrupt" || type === "final") {
                            try {
                                const parsedData = JSON.parse(event.data);
                                console.log("✅ Parsed event data:", parsedData);

                                if (parsedData.ERROR && typeof parsedData.ERROR === 'string') {
                                    throw new Error(parsedData.ERROR);
                                }

                                if (parsedData.data.query) {
                                    sqlQuery = parsedData.data.query;
                                }

                                if (parsedData.data.response) {
                                    accumulatedText += parsedData.data.response;
                                    console.log('Accumulated text length:', accumulatedText.length, 'Has pipe:', accumulatedText.includes('|'));

                                    setMessages(prevMessages => {
                                        const updatedMessages = [...prevMessages];
                                        const lastIndex = updatedMessages.length - 1;

                                        console.log('About to call styleText...');
                                        updatedMessages[lastIndex] = {
                                            text: styleText(accumulatedText),
                                            sender: "bot",
                                            ...(sqlQuery && { query: sqlQuery }),
                                        };

                                        return updatedMessages;
                                    });
                                } else if (parsedData.data) {
                                    accumulatedText += parsedData.data;
                                    console.log('Accumulated data length:', accumulatedText.length, 'Has pipe:', accumulatedText.includes('|'));

                                    setMessages(prevMessages => {
                                        const updatedMessages = [...prevMessages];
                                        const lastIndex = updatedMessages.length - 1;

                                        console.log('About to call styleText (data path)...');
                                        updatedMessages[lastIndex] = {
                                            text: styleText(accumulatedText),
                                            sender: "bot",
                                            ...(sqlQuery && { query: sqlQuery }),
                                        };

                                        return updatedMessages;
                                    });
                                }

                                if (parsedData.data.graph_html && type === "final") {
                                    fetch(`http://${API_HOST}:5010${parsedData.data.graph_html}`, {
                                        method: "GET",
                                    })
                                        .then(graphResponse => {
                                            if (!graphResponse.ok) {
                                                throw new Error("Failed to fetch graph");
                                            }
                                            return graphResponse.text();
                                        })
                                        .then(graphHtml => {
                                            setMessages(prevMessages => [
                                                ...prevMessages,
                                                {
                                                    type: "iframe-html",
                                                    html: graphHtml,
                                                    sender: "bot",
                                                }
                                            ]);
                                        })
                                        .catch(graphError => {
                                            console.error("Error fetching graph:", graphError);
                                            setMessages(prevMessages => [
                                                ...prevMessages,
                                                {
                                                    type: "iframe",
                                                    url: buildApiUrl('NOVA', parsedData.data.graph_html),
                                                    sender: "bot",
                                                }
                                            ]);
                                        });
                                }
                            } catch (error) {
                                console.error("Error processing event data:", error);
                            }
                        }
                    }
                });

                const reader = response.body.getReader();
                const decoder = new TextDecoder();

                console.log("📖 Starting to read stream...");
                while (true) {
                    const { value, done } = await reader.read();
                    if (done) {
                        console.log("✔️ Stream reading complete");
                        break;
                    }

                    const chunk = decoder.decode(value, { stream: true });
                    console.log("📦 Chunk received, length:", chunk.length);
                    parser.feed(chunk);
                }
            } catch (streamError) {
                console.error("Stream processing error:", streamError);
                throw streamError;
            }
        } catch (error) {
            console.error("Error in handleSendMessage:", error);
            setMessages(prevMessages => [
                ...prevMessages,
                {
                    text: error.message || "Error connecting to server.",
                    sender: "bot"
                }
            ]);
        } finally {
            setIsBotTyping(false);
            setFiles([]); // Clear files after sending
            if (fileInputRef.current) {
                fileInputRef.current.value = ""; // Reset file input
            }
        }
    };

      const handleFileUpload = async (event) => {
    const selectedFiles = Array.from(event.target.files);
    if (selectedFiles.length === 0) return;

    setFiles(prev => [...prev, ...selectedFiles]);
    setIsUploading(true);
    setUploadSuccess(null);

    try {
        // Create FormData for upload
        const formData = new FormData();
        selectedFiles.forEach(file => {
            formData.append('files', file);
        });
        formData.append('collection_name', `${userId}_Nova`);

        // Upload to RAG endpoint
        const response = await fetch(buildApiUrl('UPLOAD', '/upload-documents'), {
            method: 'POST',
            body: formData,
        });

        if (!response.ok) {
            throw new Error(`Upload failed with status: ${response.status}`);
        }

        setUploadSuccess(true);

        // Add success message to chat
        const fileMessages = selectedFiles.map(file => ({
            type: "file",
            fileName: file.name,
            fileType: file.type,
            fileURL: URL.createObjectURL(file),
            sender: "user",
        }));

        setMessages(prevMessages => [
            ...prevMessages,
            ...fileMessages,
            {
                text: `${selectedFiles.length > 1 ? 'Files have' : 'File has'} been successfully uploaded. You can now ask questions about ${selectedFiles.length > 1 ? 'their' : 'its'} content.`,
                sender: "bot",
            },
        ]);

    } catch (error) {
        console.error("Error uploading files:", error);
        setUploadSuccess(false);

        // Add error message to chat
        const fileMessages = selectedFiles.map(file => ({
            type: "file",
            fileName: file.name,
            fileType: file.type,
            fileURL: URL.createObjectURL(file),
            sender: "user",
        }));

        setMessages(prevMessages => [
            ...prevMessages,
            ...fileMessages,
            {
                text: `Sorry, there was an error uploading your ${selectedFiles.length > 1 ? 'files' : 'file'}: ${error.message}. Please try again.`,
                sender: "bot",
            },
        ]);
    } finally {
        setIsUploading(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = ""; // Reset file input
        }
    }
};

    const handleContinueInNova = () => {
        navigate("/nova", { state: { chatMessages: messages, cust_id } });
    };

    const handleRateMessage = (messageIndex, isGood) => {
        if (messages[messageIndex].sender !== 'bot' || ratedMessages[messageIndex]) {
            return;
        }

        console.log(isGood ? "Good answer" : "Bad answer");

        setRatedMessages(prev => ({
            ...prev,
            [messageIndex]: isGood ? 'good' : 'bad'
        }));
    };

    return (
        <div className="fixed bottom-5 right-5 z-[1000] font-sans">
            {!isOpen && (
                <button
                    className="w-[50px] h-[50px] rounded-full bg-primary text-white border-none flex items-center justify-center cursor-pointer shadow-lg transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] hover:scale-105 hover:shadow-xl"
                    onClick={() => setIsOpen(true)}
                >
                    <MessageCircle size={24} />
                </button>
            )}

            {isOpen && (
                <div
                    className="fixed bottom-[30px] right-5 bg-white rounded-2xl shadow-xl border border-gray-100 flex flex-col overflow-hidden transition-all duration-300 ease-[cubic-bezier(0.34,1.56,0.64,1)] animate-fadeIn md:right-5 md:bottom-[30px] sm:right-[5vw] sm:bottom-[70px] sm:w-[90vw] sm:h-[60vh]"
                    style={{
                        width: isExpanded ? '1250px' : '350px',
                        height: isExpanded ? '800px' : '500px',
                        backgroundColor:'white'
                    }}
                >
                    <div className="flex justify-between items-center p-4 bg-white border-b border-gray-100">
                        <div className="flex items-center gap-2.5">
                            <div className="w-2.5 h-2.5 bg-primary rounded-full"></div>
                            <span className="font-semibold text-base text-primary">NOVA</span>
                        </div>
                        <div className="flex gap-2">
                            <ArrowUpRight
                                size={22}
                                className="cursor-pointer text-gray-500 flex items-center justify-center p-1 rounded hover:text-gray-900"
                                title="Continue in NOVA"
                                onClick={handleContinueInNova}
                            />
                            <button
                                type="button"
                                className="bg-transparent border-none cursor-pointer text-gray-500 flex items-center justify-center p-1 rounded hover:text-gray-900"
                                onClick={handleExpand}
                            >
                                {isExpanded ? <Minimize size={18} /> : <Maximize size={18} />}
                            </button>
                            <button
                                className="bg-transparent border-none cursor-pointer text-gray-500 flex items-center justify-center p-1 rounded hover:text-gray-900"
                                onClick={handleClose}
                            >
                                <X size={18} />
                            </button>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4 bg-gray-50">
                        {messages.filter(msg => !msg.isStreaming && (msg.text?.trim() || msg.html || msg.url || msg.type)).map((msg, index) => (
                            <div key={index} className={`max-w-[85%] p-3 rounded-xl text-sm leading-6 ${msg.sender === 'user' ? 'self-end bg-primary text-white rounded-br-sm' : 'self-start bg-white text-gray-900 border border-gray-200 rounded-bl-sm shadow-sm'} animate-messageAppear relative group`}>
                                    {msg.type === "file" ? (
                                        <div className="flex items-center gap-2 p-2 bg-gray-100 rounded-lg max-w-[80%]">
                                            <FileText size={20} />
                                            <a href={msg.fileURL} target="_blank" rel="noopener noreferrer" className="text-gray-900 no-underline text-sm hover:underline">
                                                {msg.fileName.length > 15
                                                    ? msg.fileName.substring(0, 12) + "..."
                                                    : msg.fileName}
                                            </a>
                                        </div>
                                    ) : msg.type === "iframe" ? (
                                        <div className={`w-full my-2 rounded-lg overflow-hidden bg-white border border-gray-200 ${isExpanded ? 'h-[400px]' : ''}`}>
                                            <iframe
                                                src={msg.url}
                                                title="Embedded Content"
                                                className="w-full h-[300px]"
                                                frameBorder="0"
                                            ></iframe>
                                        </div>
                                    ) : msg.type === "iframe-html" ? (
                                        <div
                                            className={`w-full my-2 rounded-lg overflow-hidden bg-white border border-gray-200 ${isExpanded ? 'h-[400px]' : ''}`}
                                            dangerouslySetInnerHTML={{ __html: msg.html }}
                                        ></div>
                                    ) : (
                                        <div className="break-words relative">
                                            <span dangerouslySetInnerHTML={{ __html: msg.text }} />
                                            {msg.sender === 'bot' && (
                                                <div className="flex items-center gap-2 absolute -top-6 right-2 bg-white rounded-full py-1 px-2 shadow-sm border border-gray-200 opacity-0 transition-opacity duration-200 group-hover:opacity-100 hover:opacity-100">
                                                    <ThumbsUp
                                                        size={16}
                                                        className={`cursor-pointer transition-transform duration-200 hover:scale-110 hover:text-green-500 ${ratedMessages[index] === 'good' ? 'text-green-500' : 'text-gray-500'} ${ratedMessages[index] === 'bad' ? 'opacity-50 cursor-default' : ''}`}
                                                        onClick={() => handleRateMessage(index, true)}
                                                    />
                                                    <ThumbsDown
                                                        size={16}
                                                        className={`cursor-pointer transition-transform duration-200 hover:scale-110 hover:text-red-500 ${ratedMessages[index] === 'bad' ? 'text-red-500' : 'text-gray-500'} ${ratedMessages[index] === 'good' ? 'opacity-50 cursor-default' : ''}`}
                                                        onClick={() => handleRateMessage(index, false)}
                                                    />
                                                </div>
                                            )}

                                            {userIsAdmin && msg.query && (
                                                <BadgeInfo
                                                    size={18}
                                                    className="absolute -bottom-2 -right-2 cursor-pointer text-gray-500 bg-white rounded-full p-0.5 shadow-sm border border-gray-200 transition-all duration-200 hover:text-primary hover:scale-110"
                                                    onClick={() => toggleQueryPopup(index)}
                                                />
                                            )}

                                            {userIsAdmin && queryPopupIndex === index && (
                                                <div className="absolute right-0 bg-white border border-gray-200 rounded-lg p-3 overflow-y-auto shadow-lg z-10 animate-fadeIn" ref={popupRef}>
                                                    <strong className="block mb-2 text-gray-900">SQL Query:</strong>
                                                    <div className="font-mono text-xs whitespace-pre-wrap bg-gray-50 p-2 rounded border border-gray-200 text-gray-700">
                                                        {msg.query}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                        ))}

                        {isBotTyping && (
                            <div className="max-w-[85%] p-3 rounded-xl text-sm self-start bg-white text-gray-900 border border-gray-200 rounded-bl-sm shadow-sm flex items-center gap-1 animate-fadeIn">
                                <span className="w-2 h-2 bg-gray-500 rounded-full animate-typing-dot"></span>
                                <span className="w-2 h-2 bg-gray-500 rounded-full animate-typing-dot" style={{ animationDelay: '0.2s' }}></span>
                                <span className="w-2 h-2 bg-gray-500 rounded-full animate-typing-dot" style={{ animationDelay: '0.4s' }}></span>
                            </div>
                        )}
                        {isUploading && (
                            <div className="max-w-[85%] p-3 rounded-xl text-sm self-start bg-white text-gray-900 border border-gray-200 rounded-bl-sm shadow-sm flex items-center gap-2 animate-fadeIn">
                                <div className="flex items-center gap-1">
                                    <span className="w-2 h-2 bg-gray-500 rounded-full animate-typing-dot"></span>
                                    <span className="w-2 h-2 bg-gray-500 rounded-full animate-typing-dot" style={{ animationDelay: '0.2s' }}></span>
                                    <span className="w-2 h-2 bg-gray-500 rounded-full animate-typing-dot" style={{ animationDelay: '0.4s' }}></span>
                                </div>
                                <span>Uploading your file...</span>
                            </div>
                        )}

                        <div ref={messagesEndRef} />
                    </div>

                    <div 
                        className="flex items-center p-3 border-t border-rose-300" 
                        style={{ 
                            backgroundColor: '#f0d5d9 !important',
                            backgroundImage: 'none !important',
                            background: '#f0d5d9 !important'
                        }}
                    >
                        <input
                            type="text"
                            placeholder="Ask about your portfolio..."
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
                            className="flex-1 border border-gray-200 rounded-lg py-2.5 px-4 text-sm outline-none transition-colors duration-200 focus:border-black"
                            style={{ backgroundColor: 'white' }}
                        />
                        <label className="flex items-center justify-center cursor-pointer text-gray-500 p-2 rounded hover:text-primary mx-2">
                            <input 
                            type="file" 
                            multiple 
                            onChange={handleFileUpload} 
                            className="hidden" 
                            ref={fileInputRef}
                            />
                            <Paperclip size={18} />
                        </label>
                        <button
                            className="bg-primary text-white border-none rounded-lg w-9 h-9 flex items-center justify-center ml-2 cursor-pointer transition-all duration-200 hover:-translate-y-0.5"
                            onClick={handleSendMessage}
                        >
                            <Send size={18} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default WealthChatBot;